import { z } from 'zod';
const providerEnum = z.enum(['GMAIL', 'OUTLOOK', 'YAHOO', 'ZOHO', 'ICLOUD', 'CUSTOM']);
/**
 * `hostname` rejects protocol-relative and IP-literal inputs at the edge; the
 * worker additionally resolves and blocks private ranges before connecting.
 */
const hostSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(253)
    .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/, 'Enter a valid hostname');
/**
 * The shape every provider's *generated* app password takes: one unbroken run
 * of 16 letters and digits. Used to tell a pasted app password apart from a
 * passphrase the user chose themselves.
 */
const GENERATED_APP_PASSWORD = /^[a-z0-9]{16}$/i;
/**
 * A mailbox credential, as the user actually pasted it.
 *
 * Google shows app passwords as four space-separated groups — "abcd efgh ijkl
 * mnop" — and people copy them exactly as displayed. IMAP transmits the string
 * literally, so those spaces make the credential wrong, and the server answers
 * with a bare "Invalid credentials" that reads as though the password itself
 * were mistyped. The same goes for a stray newline picked up by a copy.
 *
 * Internal whitespace is removed only when what remains is a generated app
 * password, so a genuine passphrase containing spaces is left intact — as are
 * iCloud's hyphens, which Apple expects you to keep.
 */
const appPasswordSchema = z
    .string()
    .min(1, 'Password or app password is required')
    .max(512)
    .transform((value) => {
    const trimmed = value.trim();
    const collapsed = trimmed.replace(/\s+/g, '');
    return GENERATED_APP_PASSWORD.test(collapsed) ? collapsed : trimmed;
})
    .refine((value) => value.length > 0, 'Password or app password is required');
export const createMailAccountSchema = z.object({
    label: z.string().trim().min(1, 'Give this mailbox a name').max(80),
    emailAddress: z.string().trim().toLowerCase().email('A valid email address is required').max(255),
    provider: providerEnum.default('CUSTOM'),
    imapHost: hostSchema,
    imapPort: z.coerce.number().int().min(1).max(65535).default(993),
    imapSecure: z.boolean().default(true),
    /** Defaults to emailAddress when omitted. */
    username: z.string().trim().max(255).optional(),
    password: appPasswordSchema,
    folder: z.string().trim().min(1).max(255).default('INBOX'),
    syncEnabled: z.boolean().default(true),
    /**
     * Only mail received on or after this date is ever fetched. An established
     * inbox can hold tens of thousands of old messages; without a floor the
     * first sync would pull all of them and visit every link inside.
     */
    syncFromDate: z.coerce.date().default(() => new Date()),
});
export const updateMailAccountSchema = z.object({
    label: z.string().trim().min(1).max(80).optional(),
    imapHost: hostSchema.optional(),
    imapPort: z.coerce.number().int().min(1).max(65535).optional(),
    imapSecure: z.boolean().optional(),
    username: z.string().trim().max(255).optional(),
    /** Omit to leave the stored credential untouched. */
    password: appPasswordSchema.optional(),
    folder: z.string().trim().min(1).max(255).optional(),
    syncEnabled: z.boolean().optional(),
    syncFromDate: z.coerce.date().optional(),
});
/** Verify credentials before saving, so users never store a broken mailbox. */
export const testMailAccountSchema = z.object({
    emailAddress: z.string().trim().toLowerCase().email(),
    imapHost: hostSchema,
    imapPort: z.coerce.number().int().min(1).max(65535).default(993),
    imapSecure: z.boolean().default(true),
    username: z.string().trim().max(255).optional(),
    password: appPasswordSchema,
    folder: z.string().trim().min(1).max(255).default('INBOX'),
});
//# sourceMappingURL=mailAccount.js.map